import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown, TextInput } from 'dbi-pearl-ui-kit';
import { useVendorContext } from 'vendor/contexts/VendorContext';
import stateOptions from 'vendor/constants/select-options/states';
import { PearlError, loadAutocomplete, FormRow } from 'framework';
import AddressAutocompleteStyled from './AddressAutoCompleteNew.styled';
import { formFieldNames } from '../Webstore/ServiceAreaForm/Functions/ServiceAreaForm.Functions';

function TextInputComponent(props) {
    const { fieldName, fieldLabel } = props;

    return <TextInput id={fieldName} formFieldName={fieldName} providedLabel={fieldLabel} />;
}

const AddressAutoComplete = (props) => {
    const { addressLine1Label, cityLabel, stateLabel, zipcodeLabel, crawledVendorAddress } = props;

    const [queryRef, setQueryRef] = useState(null);
    const [queryResult, setQueryResult] = useState('');

    const { t } = useTranslation();
    // Signature Collection vendors may be outside the US/Canada: addresses from any country are
    // suggested and the state is free text instead of the US/Canada dropdown.
    const { vendor } = useVendorContext();
    const isSignature = vendor?.vendorType === 'signature';

    const {
        setValue,
        formState: { isSubmitted },
        trigger,
    } = useFormContext();

    const formName = `${t('Forms.Names.BusinessAddress')}`;
    const [addressLine1FieldName, cityFieldName, stateFieldName, zipcodeFieldName] = [
        formFieldNames.ServiceAddressStreet,
        formFieldNames.ServiceAddressCity,
        formFieldNames.ServiceAddressState,
        formFieldNames.ServiceAddressZipcode,
    ];

    useEffect(() => {
        if (crawledVendorAddress) {
            const { addressLine, city, state, zipcode } = crawledVendorAddress;

            if (addressLine) setValue(addressLine1FieldName, addressLine, { shouldValidate: true });
            if (city) setValue(cityFieldName, city, { shouldValidate: true });
            if (state) setValue(stateFieldName, state, { shouldValidate: true });
            if (zipcode) setValue(zipcodeFieldName, zipcode, { shouldValidate: true });

            // Trigger validation if form was already submitted
            // if (isSubmitted) {
            //     trigger([addressLine1FieldName, cityFieldName, stateFieldName, zipcodeFieldName]);
            // }
        }
    }, [
        crawledVendorAddress,
        addressLine1FieldName,
        cityFieldName,
        stateFieldName,
        zipcodeFieldName,
    ]);

    useEffect(() => {
        if (queryRef) {
            const autocompleteOptions = {
                searchTypes: ['address'],
                setFields: ['address_components'],
                focusRef: `#${addressLine1FieldName}`,
                scriptId: 'address-autocomplete',
                anyCountry: isSignature,
            };
            loadAutocomplete(setQueryResult, queryRef, autocompleteOptions);
        } else {
            const ref = document.querySelector(`#${addressLine1FieldName}`);
            setQueryRef(ref);
        }
    }, [queryRef, addressLine1FieldName, isSignature]);

    useEffect(() => {
        if (queryResult && queryResult.address_components) {
            let address1 = '';
            let city = '';
            let state = '';
            let zipcode = '';

            // The dropdown/search use 2-letter codes for the US and Canada; elsewhere the region's
            // full name reads better than Google's abbreviation.
            const country = queryResult.address_components.find((f) => f.types.includes('country'));
            const useStateCode = !isSignature || ['US', 'CA'].includes(country?.short_name);

            for (const field of queryResult.address_components) {
                const fieldType = field.types[0];

                switch (fieldType) {
                    case 'street_number': {
                        address1 = `${field.long_name} `;
                        break;
                    }
                    case 'route': {
                        address1 += field.short_name;
                        break;
                    }
                    case 'locality': {
                        city = field.long_name;
                        break;
                    }
                    case 'administrative_area_level_1': {
                        state = useStateCode ? field.short_name : field.long_name;
                        break;
                    }
                    case 'postal_code': {
                        zipcode = `${field.long_name || ''}`;
                        break;
                    }
                    case 'postal_code_suffix': {
                        zipcode = `${zipcode}-${field.long_name || ''}`;
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }

            setValue(addressLine1FieldName, address1, { shouldValidate: true });
            setValue(cityFieldName, city, { shouldValidate: true });
            setValue(zipcodeFieldName, zipcode, { shouldValidate: true });
            setValue(stateFieldName, state, { shouldValidate: true });

            // trigger revalidate after autofill if form isSubmitted
            // - remain consistent with validationMode and remove
            // any existing validation errors
            if (isSubmitted) {
                trigger();
            }
        }
    }, [
        queryResult,
        addressLine1FieldName,
        cityFieldName,
        zipcodeFieldName,
        stateFieldName,
        isSignature,
        setValue,
        isSubmitted,
        trigger,
    ]);

    return (
        <AddressAutocompleteStyled className="formComponent">
            <PearlError name={formName} />
            <div className="addressLines">
                <FormRow
                    columns={[
                        <div key={0}>
                            <TextInputComponent
                                fieldLabel={addressLine1Label}
                                fieldName={addressLine1FieldName}
                            />
                            <PearlError name={addressLine1FieldName} />
                        </div>,
                        <div className="cityEntry" key={1}>
                            <TextInputComponent fieldLabel={cityLabel} fieldName={cityFieldName} />
                            <PearlError name={cityFieldName} />
                        </div>,
                    ]}
                    className="addressAutoComplete-input-wrapper"
                />
            </div>
            <div className="cityStateZipEntry">
                <FormRow
                    columns={[
                        <div className="stateDropdown" key={1}>
                            {isSignature ? (
                                <TextInputComponent
                                    fieldName={stateFieldName}
                                    fieldLabel={t('SignaturePlans.AddressStateLabel')}
                                />
                            ) : (
                                <Dropdown
                                    formFieldName={stateFieldName}
                                    id={stateFieldName}
                                    providedLabel={stateLabel}
                                >
                                    {stateOptions.map((state) => (
                                        <option key={state.value} value={state.value}>
                                            {state.label}
                                        </option>
                                    ))}
                                </Dropdown>
                            )}
                            <PearlError name={stateFieldName} />
                        </div>,
                        <div className="zipcodeEntry" key={2}>
                            <TextInputComponent
                                fieldName={zipcodeFieldName}
                                fieldLabel={zipcodeLabel}
                            />
                            <PearlError name={zipcodeFieldName} />
                        </div>,
                    ]}
                    className="addressAutoComplete-input-wrapper"
                />
            </div>
        </AddressAutocompleteStyled>
    );
};

export { AddressAutoComplete };
